import { useMemo, useState } from 'react';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { categoryFormSelectors } from 'Douleutaras/redux/data/v2/categoryForm/categoryFormSlice';
import { CategoryAttribute } from 'Douleutaras/redux/data/v2/categoryForm/types';
import { useFormContext } from 'react-hook-form';
import isEqual from 'lodash/isEqual';

import { StepStatus } from './post-job';

export enum Operands {
  EQUAL = 'equal',
  ANY = 'any',
  IN = 'in',
  GT = 'gt',
  LT = 'lt'
}

export const filterCallOutFee = ({ include }: { include: boolean }) => (question: CategoryAttribute) => {
  const feeExists = parseFloat(question.attribute.call_out_fee) > 0;
  if (include) { // Return ONLY questions with call_out_fee
    return feeExists;
  }

  // return questions WITHOUT call_out_fee
  return !feeExists;
};

export interface QuestionChoices {
  [key: string]: string & Array<string>;
}

enum OPERATOR {
  OR = 'or',
  AND = 'and'
}

interface QuestionStepHelpers {
  categoryId: string;
  currentQuestion: CategoryAttribute;
  questionsStepHistory: Array<number>;
  setQuestionsStepHistory: React.Dispatch<React.SetStateAction<Array<number> | undefined>>;
  setStepStatus: React.Dispatch<React.SetStateAction<StepStatus>>;
  questionsSorted: Array<CategoryAttribute>;
  showOnlyCallOutFeeQuestions: boolean;
  onInnerProgressChange?(ratio: number): void;
  onSubmit(hasMoreQuestions: boolean): void;
}

interface DecideNextQuestion {
  // Questions that are already answered by the user
  userSelectedQuestions: { [key: number]: Array<string> | string };
  // Question we want to examine
  question: CategoryAttribute;
}

const isValidSingleAnswer = (answer): boolean => {
  return !Array.isArray(answer) || answer.length <= 1;
};

const matchesCondition = (answer, expectedValue: string): boolean => {
  return isEqual(expectedValue, answer) || isEqual(expectedValue, answer?.[0]);
};

// Logic which decides the next question based on Operators and Conditions
export const decideNextQuestion = ({ userSelectedQuestions, question }: DecideNextQuestion) => {
  const matchedConditions = question?.conditions.filter(
    conditionsList => {
      const conditionCheck = condition => {
        const conditionIds = Array.isArray(condition.value)
          ? condition.value.map(val => String(val.id))
          : [ String(condition.value.id) ];

        switch (condition.operand) {
          default:
          // @TODO Implement all operands when and if they are used
          case Operands.ANY:
            return userSelectedQuestions[condition.attribute]
              && conditionIds.some(conditionId => userSelectedQuestions[condition.attribute].includes(conditionId));
          case Operands.EQUAL:

            // Multi values not allowed when EQUAL (expecting one)
            if (!isValidSingleAnswer(userSelectedQuestions[condition.attribute])) {
              return false;
            }

            return matchesCondition(userSelectedQuestions[condition.attribute], conditionIds[0]);
        }
      };

      // Checks condition logic and tries to match
      switch (conditionsList.condition_logic) {
        default:
        case OPERATOR.OR:
          if (conditionsList.conditions.length === 0) {
            return false;
          }

          return conditionsList?.conditions.some(conditionCheck);
        case OPERATOR.AND:
          if (question.condition_hide && conditionsList.conditions.length === 0) {
            return false;
          }

          if (!question.condition_hide && conditionsList.conditions.length === 0) {
            return false;
          }

          return conditionsList?.conditions.every(conditionCheck);
      }
    }
  );

  // When admin selected "show if" or "hide if" but conditions are blank
  // (Hide if): (has conditions) + (conditions are met) ==> HIDE
  if (!question.condition_hide && question.conditions.length > 0 && matchedConditions.length > 0) {
    return false;
  }

  // (Hide if): (no conditions) ==> SHOW
  if (!question.condition_hide && question.conditions.length === 0) {

    return true;
  }

  // (Hide if): (conditions are not matched) ==> SHOW
  if (!question.condition_hide && matchedConditions.length === 0) {

    return true;
  }

  // (Hide if): (conditions are matched) ==> HIDE
  if (!question.condition_hide && matchedConditions.length > 0) {

    return false;
  }

  // (Show if): (conditions are matched) ==> SHOW
  if (question.condition_hide && matchedConditions.length > 0) {

    return true;
  }

  return false;
};

const useQuestionStepHelpers = ({
                                  categoryId,
                                  currentQuestion,
                                  onSubmit,
                                  questionsStepHistory,
                                  setQuestionsStepHistory,
                                  setStepStatus,
                                  questionsSorted,
                                  showOnlyCallOutFeeQuestions
}: QuestionStepHelpers) => {

  const { watch, setValue } = useFormContext();
  const [ questionId, setQuestionId ] = useState<number>();

  const categoryForm = useAppSelector(state => categoryFormSelectors.selectById(state, categoryId));

  const setHistorySteps = (questionsSortedEffect, currentQuestionId) => {
    // Find the smallest initial step
    if (!currentQuestionId) {
      // When coming back with history, pop the last history item as we already landed on it

      const prevQuestionId = questionsStepHistory
        .slice(questionsStepHistory.length - 1)[0];

      const nextQuestion = determineNextQuestion(prevQuestionId, questionsSortedEffect);

      currentQuestionId = nextQuestion ? nextQuestion.id : prevQuestionId;

      // When coming from a "Back" button of a different step
      if (questionsStepHistory.length > 0 && !nextQuestion) {
        setQuestionsStepHistory(questionsStepHistory.slice(0, -1));
      }

      setQuestionId(currentQuestionId);
    }

    return currentQuestionId;
  };

  const sortedQuestions = useMemo(() => {
    return categoryForm
      ? [...categoryForm.attributes]
        .sort((a, b) => a.order - b.order)
        .filter(filterCallOutFee({ include: showOnlyCallOutFeeQuestions }))
      : [];
  }, [categoryForm]);

  const findQuestionById = useMemo(() => (currentId: number) => {
    return sortedQuestions.find(question => question.id === currentId);
  }, [sortedQuestions]);

  const determineNextQuestion = (currentQuestionId = undefined, questionsSortedOverride = undefined) => {
    const questions = questionsSortedOverride ? questionsSortedOverride : questionsSorted;
    const questionIterationHistory = [];

    return questions.find(question => {
      if (
        question.id === currentQuestionId
        || questionsStepHistory.includes(question.id)
        || questionIterationHistory.includes(question.id)
      ) {
        return false;
      }
      questionIterationHistory.push(question.id);

      const newChoices = watch() as QuestionChoices;

      return decideNextQuestion({ userSelectedQuestions: newChoices.questions, question });
    });
  };

  const switchToNextStep = () => {
    // Check if there is a next step
    const nextQuestion = determineNextQuestion(currentQuestion.id);
    const hasMoreQuestions = nextQuestion !== undefined;

    // Sets the current question step (next)
    setQuestionsStepHistory(prevSteps => [ ...prevSteps, currentQuestion.id ]);
    if (hasMoreQuestions) {
      setStepStatus(nextQuestion.attribute.required ? StepStatus.HAS_NEXT : StepStatus.CAN_SKIP);
      setQuestionId(nextQuestion.id);
    } else {
      setQuestionId(currentQuestion.id);
      setStepStatus(StepStatus.IS_FINAL);
    }

    // fire callback from parent
    onSubmit(hasMoreQuestions);
  };

  // Will go to the previous questions based on step history
  const switchStepBack = () => {
    const prevQuestionId = questionsStepHistory.slice(-1)[0];
    setQuestionId(prevQuestionId);
    setQuestionsStepHistory(questionsStepHistory.slice(0, -1));
    const values = watch();
    const newQuestions = { ...values.questions, [currentQuestion.attribute.id]: undefined };
    // Remove the current question from the answered ones, this will make sure it will not create dependency confusion
    setValue('questions', newQuestions);
  };

  // Fires when a user chooses an option (choice)
  const onSelectChoice = (choiceId: number, submitAfterSelect: boolean,
                          onChange, isSelected: boolean, value = [], isMultiChoice: boolean, reValidate): void => {
    const formattedChoiceId = isMultiChoice
      ? value.includes(String(choiceId))
        ? value.filter(val => val !== String(choiceId))
        : [...value, String(choiceId)]
      : String(choiceId);

    onChange(formattedChoiceId);

    // Validate the form after change to enable/disable the submit button
    reValidate();

    if (submitAfterSelect) {
      switchToNextStep();
    }
  };

  return {
    categoryForm,
    determineNextQuestion,
    filterCallOutFee,
    setHistorySteps,
    questionId,
    questionsSorted,
    findQuestionById,
    sortedQuestions,
    switchToNextStep,
    onSelectChoice,
    switchStepBack
  };
};

export default useQuestionStepHelpers;
